import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import NumericInput from "@/components/ui/NumericInput";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Shield, Info, Building2, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import type { MortgageData } from "@/types/calculator";
import { calculateMortgageData } from "@/utils/calculator/mortgageCalculator";
import { AutoCalculateOptions } from "../../utils/types";
import { formatPercentage, parsePercentage } from "../../utils/formatters";

interface PropertyTaxPIDInputProps {
  mortgageData: MortgageData;
  setMortgageData: React.Dispatch<React.SetStateAction<MortgageData>>;
  autoCalculate: AutoCalculateOptions;
  setAutoCalculate: React.Dispatch<React.SetStateAction<AutoCalculateOptions>>;
}

const PropertyTaxPIDInput: React.FC<PropertyTaxPIDInputProps> = ({ 
  mortgageData, 
  setMortgageData, 
  autoCalculate, 
  setAutoCalculate 
}) => {
  const [isBreakdownOpen, setIsBreakdownOpen] = useState(false);
  
  // Check if VA 100% disability exemption applies
  const isVA100Disabled = mortgageData.loanType === 'va' && mortgageData.vaDisabilityStatus === 'full';
  
  // Display property tax - force 0 for VA 100% disabled, otherwise use current value or default
  const displayPropertyTax = isVA100Disabled ? 0 : (mortgageData.propertyTax || 2.4);
  const displayPIDRate = mortgageData.pidRate || 0.54;
  
  // Calculate total effective rate
  const totalRate = displayPropertyTax + (mortgageData.includePID ? displayPIDRate : 0);
  
  // Disable inputs for VA 100% disabled veterans (property tax only, not PID)
  const isPropertyTaxDisabled = autoCalculate.propertyTax || isVA100Disabled;

  // Custom formatter for property tax to show exactly 2 decimal places
  const formatPropertyTaxPercentage = (percent: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(percent / 100);
  };

  const handlePropertyTaxChange = (value: number) => {
    // Prevent changes for VA 100% disabled veterans
    if (isVA100Disabled) {
      console.log('VA 100% disability detected - property tax forced to 0%');
      return;
    }
    
    setMortgageData(prev => calculateMortgageData({
      ...prev,
      propertyTax: value
    }));
  };

  const handlePIDRateChange = (value: number) => {
    setMortgageData(prev => calculateMortgageData({
      ...prev,
      pidRate: value
    }));
  };

  const handleIncludePIDChange = (checked: boolean) => {
    setMortgageData(prev => calculateMortgageData({
      ...prev,
      includePID: checked
    }));
  };

  // Example calculation for $400k home
  const exampleHomeValue = 400000;
  const annualPropertyTax = (displayPropertyTax / 100) * exampleHomeValue;
  const annualPID = mortgageData.includePID ? (displayPIDRate / 100) * exampleHomeValue : 0;
  const totalAnnualTax = annualPropertyTax + annualPID;

  return (
    <div className="space-y-4">
      {/* VA 100% Disability Notice */}
      {isVA100Disabled && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2 text-blue-700 text-sm">
            <Shield className="w-4 h-4 mt-0.5" />
            <div>
              <div className="font-medium">100% VA Disability Property Tax Exemption</div>
              <div className="text-xs mt-1">
                Property taxes are exempt, but PID assessments may still apply as they are not technically "taxes."
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Property Tax Rate Section */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Label className="text-sm font-medium">
              Property Tax Rate
              {isVA100Disabled && (
                <span className="ml-2 text-xs text-blue-600 font-medium">(Exempted)</span>
              )}
            </Label>
            <div className="flex items-center space-x-2">
              <Switch
                checked={autoCalculate.propertyTax}
                onCheckedChange={(checked) => {
                  if (!isVA100Disabled) {
                    setAutoCalculate(prev => ({
                      ...prev,
                      propertyTax: checked
                    }));
                  }
                }}
                disabled={isVA100Disabled}
              />
              <span className="text-xs text-homebuyer-gray">Auto</span>
            </div>
          </div>
          <NumericInput 
            value={displayPropertyTax}
            onChange={handlePropertyTaxChange}
            formatValue={formatPropertyTaxPercentage}
            parseValue={parsePercentage}
            min={0}
            max={15}
            disabled={isPropertyTaxDisabled}
            className="w-32"
            inputClassName="text-right overflow-ellipsis"
            suffix="%"
          />
        </div>
        <Slider
          defaultValue={[displayPropertyTax]}
          min={0}
          max={5}
          step={0.05}
          disabled={isPropertyTaxDisabled}
          value={[displayPropertyTax]}
          onValueChange={(value) => {
            const newValue = Array.isArray(value) ? value[0] : value;
            handlePropertyTaxChange(newValue);
          }}
          className="my-2"
        />
      </div>

      {/* PID Section */}
      <div className="space-y-2 border-t pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Building2 className="w-4 h-4 text-amber-600" />
            <Label className="text-sm font-medium">
              PID Assessment
            </Label>
            <div className="flex items-center space-x-2">
              <Switch
                checked={mortgageData.includePID}
                onCheckedChange={handleIncludePIDChange}
              />
              <span className="text-xs text-homebuyer-gray">Include</span>
            </div>
          </div>
          <NumericInput 
            value={displayPIDRate}
            onChange={handlePIDRateChange}
            formatValue={formatPropertyTaxPercentage}
            parseValue={parsePercentage}
            min={0}
            max={2}
            disabled={!mortgageData.includePID}
            className="w-32"
            inputClassName="text-right overflow-ellipsis"
            suffix="%"
          />
        </div>
        
        {mortgageData.includePID && (
          <Slider
            defaultValue={[displayPIDRate]}
            min={0}
            max={1}
            step={0.01}
            value={[displayPIDRate]}
            onValueChange={(value) => {
              const newValue = Array.isArray(value) ? value[0] : value;
              handlePIDRateChange(newValue);
            }}
            className="my-2"
          />
        )}

        <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded-lg border border-amber-200">
          <div className="flex items-start gap-1">
            <Info className="w-3 h-3 mt-0.5" />
            <div>
              PID (Public Improvement District) assessments are separate from property taxes and typically fund community amenities, infrastructure, and maintenance.
            </div>
          </div>
        </div>
      </div>

      {/* Total Rate Display */}
      <div className="p-3 bg-card border border-border rounded-lg">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-foreground">Total Effective Rate</span>
          <span className="text-lg font-bold text-foreground">
            {formatPropertyTaxPercentage(totalRate)}
          </span>
        </div>
      </div>

      {/* Tax Breakdown */}
      <Collapsible open={isBreakdownOpen} onOpenChange={setIsBreakdownOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="outline" className="w-full justify-between">
            <span className="flex items-center gap-2">
              🇺🇸 🏡 Property Tax Breakdown
            </span>
            {isBreakdownOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4">
          <div className="p-4 bg-card border border-border rounded-lg">
            <div className="text-sm text-muted-foreground mb-3">
              Example for ${exampleHomeValue.toLocaleString()} home value:
            </div>
            
            <div className="space-y-2 text-sm text-foreground">
              <div className="flex justify-between">
                <span>Property Taxes ({formatPropertyTaxPercentage(displayPropertyTax)}):</span>
                <span className={isVA100Disabled ? "line-through text-muted-foreground" : ""}>
                  ${annualPropertyTax.toLocaleString()}/year
                </span>
              </div>
              
              {mortgageData.includePID && (
                <div className="flex justify-between">
                  <span>PID Assessment ({formatPropertyTaxPercentage(displayPIDRate)}):</span>
                  <span>${annualPID.toLocaleString()}/year</span>
                </div>
              )}
              
              <div className="border-t pt-2 flex justify-between font-medium">
                <span>Total Annual:</span>
                <span>${(isVA100Disabled ? annualPID : totalAnnualTax).toLocaleString()}</span>
              </div>
              
              <div className="flex justify-between text-muted-foreground">
                <span>Monthly:</span>
                <span>${((isVA100Disabled ? annualPID : totalAnnualTax) / 12).toLocaleString()}</span>
              </div>
            </div>

            {isVA100Disabled && annualPID > 0 && (
              <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                <strong>Note:</strong> As a 100% disabled veteran, your property taxes are exempt but PID assessments still apply.
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Status Messages */}
      {isVA100Disabled && (
        <div className="text-xs p-2 rounded-lg border bg-green-50 border-green-200 text-green-700">
          Property tax rate set to 0% due to 100% VA disability exemption
        </div>
      )}
    </div>
  );
};

export default PropertyTaxPIDInput;